import React, { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import ReactSlider from 'react-slider'
import publicApi from '../services/public.api'
import { useCart } from '../context/CartContext'
import { useLocation } from '../context/LocationContext'
import { animateFlyToCart, bumpCartIcon } from '../utils/cartAnimation'
import ProductCardQuantity, { isProductHasVariants } from './ProductCardQuantity'
import ProductCardPriceQtyRow from './ProductCardPriceQtyRow'
import { useProductCardCart } from '../hooks/useProductCardCart'

const ITEMS_PER_PAGE = 20;

const ShopSection = () => {
    const [searchParams] = useSearchParams();
    const categoryFromUrl = searchParams.get('category');
    const searchFromUrl = searchParams.get('search') || '';
    
    const [grid, setGrid] = useState(false)
    const [active, setActive] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || 'all')
    const [sortBy, setSortBy] = useState('popular')
    const [priceRange, setPriceRange] = useState([0, 1000])
    const [maxPrice, setMaxPrice] = useState(1000)
    const [products, setProducts] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [categoriesLoading, setCategoriesLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [addedProductId, setAddedProductId] = useState(null)
    
    const { addToCart } = useCart();
    
    // Location-based pricing - use isCasaRegion and getPrice from context
    const { isCasaRegion, getPrice, priceRegion } = useLocation();
    
    const getLocationPrice = (product) => getPrice(product);

    const {
        getSelection,
        updateSelection,
        prepareCartItem,
        weightHintId,
        showWeightHint,
        getDisplayPrice,
        getWeightText,
    } = useProductCardCart(getLocationPrice);

    // Sync filters when URL changes (header search / category dropdown)
    useEffect(() => {
        setSelectedCategory(categoryFromUrl || 'all');
    }, [categoryFromUrl]);

    const handleAddToCart = (product, sourceEl) => {
        const built = prepareCartItem(product);
        if (built.error) {
            showWeightHint(product.id);
            return;
        }

        addToCart(built.product, built.quantity);

        const isDesktop = typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(min-width: 992px)').matches
            : true;
        const cartEl = document.getElementById(isDesktop ? 'header-cart-icon-desktop' : 'header-cart-icon-mobile')
            || document.getElementById('header-cart-icon-desktop')
            || document.getElementById('header-cart-icon-mobile');
        if (cartEl) {
            bumpCartIcon(cartEl);
        }
        if (sourceEl && cartEl) {
            animateFlyToCart({ sourceEl, cartEl });
        }
        window.dispatchEvent(new Event('delice:cart:add'));

        setAddedProductId(product.id);
        setTimeout(() => {
            setAddedProductId(null);
        }, 1500);
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await publicApi.getCategories(priceRegion);
                if (response.success && response.data) {
                    setCategories(response.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, [priceRegion]);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await publicApi.getProducts({ region: priceRegion });
                if (response.success && response.data) {
                    const productsData = response.data.data || response.data;
                    setProducts(productsData);
                    if (productsData.length > 0) {
                        const prices = productsData.map(p => parseFloat(p.price) || 0);
                        const max = Math.ceil(Math.max(...prices) / 100) * 100;
                        setMaxPrice(max || 1000);
                        setPriceRange([0, max || 1000]);
                    }
                }
            } catch (error) {
                console.error('Error fetching products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [priceRegion]);

    const filteredProducts = useMemo(() => {
        let result = [...products];
        
        if (selectedCategory !== 'all') {
            const categoryId = parseInt(selectedCategory);
            result = result.filter(p => {
                const prodCategoryId = parseInt(p.category_id);
                return prodCategoryId === categoryId || p.category_id === selectedCategory;
            });
        }

        const searchTerm = searchFromUrl.trim().toLowerCase();
        if (searchTerm) {
            result = result.filter((p) => {
                const name = String(p.name || '').toLowerCase();
                const description = String(p.description || '').toLowerCase();
                return name.includes(searchTerm) || description.includes(searchTerm);
            });
        }
        
        result = result.filter(p => {
            const price = getLocationPrice(p);
            return price >= priceRange[0] && price <= priceRange[1];
        });
        
        switch (sortBy) {
            case 'recent':
                result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'price-low':
                result.sort((a, b) => getLocationPrice(a) - getLocationPrice(b));
                break;
            case 'price-high':
                result.sort((a, b) => getLocationPrice(b) - getLocationPrice(a));
                break;
            case 'popular':
            default:
                result.sort((a, b) => (b.sold_count || 0) - (a.sold_count || 0));
                break;
        }
        
        return result;
    }, [products, selectedCategory, searchFromUrl, priceRange, sortBy, isCasaRegion]);

    const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
    const paginatedProducts = filteredProducts.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, searchFromUrl, priceRange, sortBy]);

    const getCategoryProductCount = (categoryId) => {
        if (!products.length) return 0;
        return products.filter(p => parseInt(p.category_id) === parseInt(categoryId)).length;
    };

    const sidebarController = () => {
        setActive(!active);
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return '/assets/images/thumbs/product-img1.png';
        if (imagePath.startsWith('http')) return imagePath;
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const storageBaseUrl = API_BASE_URL.replace('/api', '');
        
        // Check if path already starts with /storage/ to avoid double prefix
        if (imagePath.startsWith('/storage/')) return `${storageBaseUrl}${imagePath}`;
        if (imagePath.startsWith('storage/')) return `${storageBaseUrl}/${imagePath}`;
        return `${storageBaseUrl}/storage/${imagePath}`;
    };

    const ProductCard = ({ product }) => {
        const discount = product.discount_percentage || (product.original_price ? 
            Math.round((1 - parseFloat(product.price) / parseFloat(product.original_price)) * 100) : null);
        const originalPrice = product.original_price || null;

        return (
            <div className="product-card shop-product-card shop-product-hover h-100 w-100 p-16 border border-gray-100 rounded-16 position-relative transition-2">
                <Link
                    to={`/product-details/${product.id}`}
                    className="product-card__thumb shop-product-thumb flex-center rounded-8 position-relative"
                >
                    <img
                        src={getImageUrl(product.image)}
                        alt={product.name}
                        className="shop-product-img product-card__thumb-img"
                        onError={(e) => {
                            e.target.src = '/assets/images/thumbs/product-img1.png';
                        }}
                    />
                    {discount && (
                        <span className="product-card__badge shop-product-badge px-8 py-4 text-sm position-absolute inset-inline-start-0 inset-block-start-0">
                            -{discount}%
                        </span>
                    )}
                </Link>
                <div className="product-card__content mt-16 w-100">
                    <h6 className="title text-lg fw-semibold mt-12 mb-8">
                        <Link
                            to={`/product-details/${product.id}`}
                            className="link text-line-2"
                        >
                            {product.name}
                        </Link>
                    </h6>
                    <div className="flex-align mb-20 mt-16 gap-6">
                        <span className="text-xs fw-medium text-gray-500">{product.rating || '4.5'}</span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                            <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-xs fw-medium text-gray-500">({product.reviews_count || '0'})</span>
                    </div>
                    {product.stock !== undefined && (
                        <div className="mt-8">
                            <div
                                className="progress w-100 bg-color-three rounded-pill h-4"
                                role="progressbar"
                                aria-valuenow={product.sold_count || 0}
                                aria-valuemin={0}
                                aria-valuemax={product.stock + (product.sold_count || 0)}
                            >
                                <div
                                    className="progress-bar shop-product-progress rounded-pill"
                                    style={{ width: `${((product.sold_count || 0) / (product.stock + (product.sold_count || 0))) * 100}%` }}
                                />
                            </div>
                            <span className="text-gray-900 text-xs fw-medium mt-8">
                                Vendu: {product.sold_count || 0}/{product.stock + (product.sold_count || 0)}
                            </span>
                        </div>
                    )}
                    <ProductCardPriceQtyRow
                        className="my-20"
                        price={(
                            <>
                                {!isCasaRegion && originalPrice && (
                                    <span className="text-gray-400 text-md fw-semibold text-decoration-line-through d-block">
                                        {originalPrice} DH
                                    </span>
                                )}
                                <span className="text-heading text-md fw-semibold product-card-price-line">
                                    <span className="product-card-price-amount">{getDisplayPrice(product).toFixed(2)} DH</span>
                                    <span className="text-gray-500 fw-normal product-card-price-unit">/{getWeightText(product) || 'Qty'}</span>
                                </span>
                            </>
                        )}
                        hint={weightHintId === product.id ? (
                            <span className="text-danger text-xs fw-medium d-block mt-4">
                                Choisissez un poids
                            </span>
                        ) : null}
                        quantityControl={(
                            <ProductCardQuantity
                                hasVariants={isProductHasVariants(product)}
                                selection={getSelection(product)}
                                onChange={(next) => updateSelection(product, next)}
                                collapsedUntilInteraction
                            />
                        )}
                    />
                    <button 
                        onClick={(e) => {
                            e.preventDefault();
                            const card = e.currentTarget.closest('.product-card');
                            const img = card ? card.querySelector('img') : null;
                            handleAddToCart(product, img);
                        }}
                        className={`product-card__cart btn ${addedProductId === product.id ? 'btn-success' : 'shop-product-btn'} text-white hover-text-white py-11 px-24 rounded-8 flex-center gap-8 fw-medium w-100`}
                        disabled={addedProductId === product.id}
                    >
                        {addedProductId === product.id ? (
                            <>Ajouté <i className="ph ph-check" /></>
                        ) : (
                            <>Ajouter au Panier <i className="ph ph-shopping-cart" /></>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    const ProductSkeleton = () => (
        <div className="product-card h-100 p-16 border border-gray-100 rounded-16">
            <div style={{ height: '200px', borderRadius: '8px', backgroundColor: '#f0f0f0' }}></div>
            <div className="mt-16">
                <div style={{ height: '20px', width: '80%', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
                <div style={{ height: '16px', width: '60%', backgroundColor: '#f0f0f0', borderRadius: '4px', marginTop: '8px' }}></div>
                <div style={{ height: '24px', width: '40%', backgroundColor: '#f0f0f0', borderRadius: '4px', marginTop: '16px' }}></div>
            </div>
        </div>
    );

    return (
        <section className="shop py-80">
            <div className={`side-overlay ${active && "show"}`}></div>
            <div className="container container-lg">
                <div className="row">
                    {/* Sidebar */}
                    <div className={`col-lg-3 ${active && "active"}`}>
                        <div className="shop-sidebar">
                            <button
                                onClick={sidebarController}
                                type="button"
                                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
                            >
                                <i className="ph ph-x" />
                            </button>
                            
                            {/* Categories */}
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Catégories
                                </h6>
                                <ul className="max-h-540 overflow-y-auto scroll-sm">
                                    <li className="mb-24">
                                        <button
                                            onClick={() => setSelectedCategory('all')}
                                            className={`text-gray-900 hover-text-main-600 ${selectedCategory === 'all' ? 'text-main-600 fw-semibold' : ''}`}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                                        >
                                            Toutes les catégories 
                                            <span className="text-gray-400">({products.length})</span>
                                        </button>
                                    </li>
                                    {categoriesLoading ? (
                                        <li className="text-gray-500">Chargement...</li>
                                    ) : (
                                        categories.map((category) => (
                                            <li key={category.id} className="mb-24">
                                                <button
                                                    onClick={() => setSelectedCategory(category.id.toString())}
                                                    className={`text-gray-900 hover-text-main-600 ${selectedCategory === category.id.toString() ? 'text-main-600 fw-semibold' : ''}`}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                                                >
                                                    {category.name} 
                                                    <span className="text-gray-400">({getCategoryProductCount(category.id)})</span>
                                                </button>
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                            
                            {/* Price Filter */}
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                                    Filtrer par Prix
                                </h6>
                                <div className="custom--range">
                                    <ReactSlider
                                        className="horizontal-slider"
                                        thumbClassName="example-thumb"
                                        trackClassName="example-track"
                                        value={priceRange}
                                        min={0}
                                        max={maxPrice}
                                        step={10}
                                        ariaLabel={['Lower thumb', 'Upper thumb']}
                                        ariaValuetext={state => `Thumb value ${state.valueNow}`}
                                        renderThumb={(props, state) => {
                                            const { key, ...restProps } = props;
                                            return <div {...restProps} key={state.index}>{state.valueNow}</div>;
                                        }}
                                        pearling
                                        minDistance={10}
                                        onChange={(value) => setPriceRange(value)}
                                    />
                                    <br />
                                    <br />
                                    <div className="flex-between flex-wrap-reverse gap-8 mt-24">
                                        <span className="text-gray-500">
                                            Prix: {priceRange[0]} DH - {priceRange[1]} DH
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Promotional Banner - Fixed Image */}
                            <div className="shop-sidebar__box border border-gray-100 rounded-8 overflow-hidden position-sticky" style={{ top: '100px' }}>
                                <div className="shop-sidebar__banner position-relative">
                                    <img 
                                        src="/assets/images/thumbs/advertise-img1.png" 
                                        alt="Promotion" 
                                        className="w-100"
                                        style={{ minHeight: '280px', objectFit: 'cover' }}
                                    />
                                    <div className="position-absolute inset-0 d-flex flex-column justify-content-center p-24" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.5))' }}>
                                        <span className="text-white text-sm fw-medium mb-8">Offre Spéciale</span>
                                        <h5 className="text-white fw-bold mb-16">Jusqu'à -30%</h5>
                                        <p className="text-white text-sm mb-16">Sur une sélection de produits</p>
                                        <Link 
                                            to="/shop" 
                                            className="btn btn-main-600 text-white py-8 px-16 rounded-8 text-sm fw-medium"
                                            style={{ width: 'fit-content' }}
                                        >
                                            Voir les offres
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Products Grid */}
                    <div className="col-lg-9">
                        {/* Toolbar */}
                        <div className="flex-between gap-16 flex-wrap mb-40">
                            <span className="text-gray-900">
                                Affichage 1-{Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)} sur {filteredProducts.length} résultats
                            </span>
                            <div className="position-relative flex-align gap-16 flex-wrap">
                                <div className="list-grid-btns flex-align gap-16">
                                    <button 
                                        onClick={() => setGrid(true)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 shop-grid-btn ${grid === true && "shop-grid-btn-active"}`}
                                    >
                                        <i className="ph-bold ph-list-dashes" />
                                    </button>
                                    <button 
                                        onClick={() => setGrid(false)}
                                        type="button"
                                        className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 shop-grid-btn ${grid === false && "shop-grid-btn-active"}`}
                                    >
                                        <i className="ph ph-squares-four" />
                                    </button>
                                </div>
                                <div className="position-relative text-gray-500 flex-align gap-4 text-14">
                                    <label htmlFor="sorting" className="text-inherit flex-shrink-0">
                                        Trier par:
                                    </label>
                                    <select 
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="form-control common-input px-14 py-14 text-inherit rounded-6 w-auto"
                                        id="sorting"
                                    >
                                        <option value="popular">Populaire</option>
                                        <option value="recent">Récent</option>
                                        <option value="price-low">Prix: Bas à Haut</option>
                                        <option value="price-high">Prix: Haut à Bas</option>
                                    </select>
                                </div>
                                <button 
                                    onClick={sidebarController}
                                    type="button"
                                    className="w-44 h-44 d-lg-none d-flex flex-center border border-gray-100 rounded-6 text-2xl sidebar-btn"
                                >
                                    <i className="ph-bold ph-funnel" />
                                </button>
                            </div>
                        </div>

                        {/* Products */}
                        {loading ? (
                            <div className="list-grid-wrapper">
                                {[...Array(8)].map((_, i) => (
                                    <ProductSkeleton key={i} />
                                ))}
                            </div>
                        ) : paginatedProducts.length === 0 ? (
                            <div className="text-center py-80">
                                <i className="ph ph-package text-6xl text-gray-300 mb-16"></i>
                                <h5 className="text-gray-500">Aucun produit trouvé</h5>
                                <p className="text-gray-400">Essayez de modifier vos filtres</p>
                            </div>
                        ) : (
                            <div className={`list-grid-wrapper ${grid && "list-view"}`}>
                                {paginatedProducts.map((product) => (
                                    <ProductCard key={product.id} product={product} />
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <ul className="pagination flex-center flex-wrap gap-16 mt-48">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="ph-bold ph-arrow-left" />
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, i) => (
                                    <li key={i} className="page-item">
                                        <button
                                            className={`page-link h-64 w-64 flex-center text-md rounded-8 fw-medium ${currentPage === i + 1 ? 'bg-main-600 text-white' : 'text-neutral-600 border border-gray-100'}`}
                                            onClick={() => setCurrentPage(i + 1)}
                                        >
                                            {String(i + 1).padStart(2, '0')}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link h-64 w-64 flex-center text-xxl rounded-8 fw-medium text-neutral-600 border border-gray-100"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <i className="ph-bold ph-arrow-right" />
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ShopSection
